/**
 * Everything the trainer has narrowed the library by: the search box, and the four facets behind
 * the toolbar's square.
 *
 * KANON `Programy v2 - D3 (0.8)` draws a panel with three tabs over the templates — **Typ**, **Tryb**,
 * **Dni** — and a fourth, **Klient**, only over the clients' plans. This module is what those tabs
 * mean; `ui/FilterPanel` is only how they are drawn.
 *
 * ── Why none of this is a server parameter ──────────────────────────────────────────────────
 * `GET /plan-template/library` DOES take `search` and `category`, and this app deliberately uses
 * neither. The read returns a trainer's own templates — tens of rows, not thousands — in one
 * response with no pagination, so filtering here buys three things a round trip cannot: the search
 * box responds without a network hop, "nothing matched" is distinguishable from "you have no plans"
 * without a second request, and the header's counts are exact rather than a page of an unknown
 * total. The category facet forces the question anyway: the canon shows two categories active at
 * once and `?category=` takes a single value, so a server-side multi-select would be N requests
 * whose results this module would have to union.
 *
 * `GET /plan-assignment` takes no parameters at all, so the clients scope was never going to ask.
 *
 * ── Three facets are the LIBRARY's and one is the LIST's ─────────────────────────────────────
 * Owner decision, 3 September 2026: Typ, Tryb and Dni survive a scope switch — they describe a plan
 * and both scopes list plans — while Klient is dropped on the way back to the templates, because a
 * template has no client to be filtered by and a facet that cannot bind would sit in the panel with
 * a count beside it and change nothing. `dropClientFacet` is that rule, and it is here rather than
 * in the hook so a test can reach it.
 *
 * ── DNI answers one question in two units, and only the templates can answer it ──────────────
 * "At most N training days per repeat" — per week for a DateBased plan, per cycle for a self-paced
 * one, which is the owner's wording and is one rule rather than two because **a weekly plan's cycle
 * IS a week**. `PlanLibraryRow.daysInCycle` carries it for both, so nothing here branches on the
 * mode.
 *
 * `PlanAssignmentSummary` carries no day count of any kind — not `daysPerWeek`, not `phaseCount` —
 * so **Dni does not narrow the clients' plans and the tab is not offered there**. Answering it from
 * the row's `sourceTemplateId` would be a fabrication: a fork diverges from the template the moment
 * the trainer edits it, and a plan built for one client directly has no source at all.
 *
 * ── `maxDays === 7` is NO LIMIT, not "seven or fewer" ────────────────────────────────────────
 * Which is also why the backend's cap on the figure cannot bind: `PlanTemplateProcessor` writes
 * `DaysPerWeek = Math.min(projection.DensestPhaseDays, 7)`, so a self-paced cycle of ten days
 * arrives as a 7. At the ceiling the facet is off, and below it a 7 fails every comparison a 10
 * would have failed.
 *
 * Pure. No React, no DOM, no i18n, no query hook.
 */

import type { PlanCategoryKey } from '~/entities/plan-draft';

import type { ClientPlanRow } from './clientPlanRow';
import type { PlanLibraryRow } from './planLibraryRow';
import type { PlanScope } from './planScope';
import { foldForSearch } from './searchFold';

/** The TRYB facet, in this app's vocabulary rather than the wire's. `all` is the facet switched off. */
export type PlanModeFacet = 'all' | 'calendar' | 'selfPaced';

/** The ceiling the DNI facet is switched off at: seven days is every week there is. */
export const NO_DAY_LIMIT = 7;

export interface PlanLibraryFilter {
  readonly search: string;
  /**
   * The categories the trainer has selected. **Empty means all**, not none — an empty selection is
   * the absence of a filter, which is what the canon's unchecked chips draw.
   */
  readonly categories: readonly PlanCategoryKey[];
  readonly mode: PlanModeFacet;
  /** 1–7, and `7` means unbounded. See the header. */
  readonly maxDays: number;
  /** Customer ids. Empty means every client; applies to the clients' plans and nowhere else. */
  readonly clients: readonly number[];
}

export const NO_FILTER: PlanLibraryFilter = {
  search: '',
  categories: [],
  mode: 'all',
  maxDays: NO_DAY_LIMIT,
  clients: [],
};

/** The four things the panel can narrow by, named as the panel's own tabs name them. */
export type PlanFilterFacet = 'type' | 'mode' | 'days' | 'client';

/**
 * Can this facet narrow THIS scope's list?
 *
 * **One rule, and both the panel and the count read it.** Two of the four bind in one scope only:
 * Klient over the clients' plans, because a template has no client, and Dni over the templates,
 * because `PlanAssignmentSummary` carries no day count of any kind and `filterClientPlanRows`
 * therefore ignores `maxDays` entirely. The panel already needed this to decide which tab to offer;
 * the count needed it too and did not have it, so a Dni set on the templates and carried across the
 * switch lit the toolbar's dot, announced the square as filtered, and narrowed nothing — with
 * `Wyczyść` the only way out, which also dropped the Typ selection the trainer could see.
 *
 * The facet's VALUE survives the switch either way; that is the owner's own rule and this only
 * governs whether it is offered and whether it counts.
 */
export function facetBinds(facet: PlanFilterFacet, scope: PlanScope): boolean {
  if (facet === 'client') return scope === 'clients';
  if (facet === 'days') return scope === 'templates';
  return true;
}

/** Is anything actually narrowing the list? The screen's fork between its two empty states. */
export function isFiltered(filter: PlanLibraryFilter, scope: PlanScope): boolean {
  return filter.search.trim() !== '' || activeFacetCount(filter, scope) > 0;
}

/**
 * How many facets are on, tab by tab — the count in each tab's pill, and summed, the dot on the
 * square.
 *
 * Dni counts as ONE however far it is dragged: the tab's pill draws `≤4`, which is the facet's
 * value rather than a number of choices, and the dot only asks whether anything is on at all. A
 * facet that cannot bind in this scope counts as none — see `facetBinds`.
 */
export function activeFacetCount(filter: PlanLibraryFilter, scope: PlanScope): number {
  return (
    filter.categories.length +
    (filter.mode === 'all' ? 0 : 1) +
    (facetBinds('days', scope) && filter.maxDays < NO_DAY_LIMIT ? 1 : 0) +
    (facetBinds('client', scope) ? filter.clients.length : 0)
  );
}

/**
 * Every facet off, the search box left alone.
 *
 * `Wyczyść` clears the PANEL, and the phrase the trainer typed is not in the panel — it is in the
 * field above the list, which has its own ✕. A footer button that silently emptied the search box
 * would take away a thing the trainer can see is set from somewhere they cannot see it.
 */
export function clearFilters(filter: PlanLibraryFilter): PlanLibraryFilter {
  return { ...NO_FILTER, search: filter.search };
}

/** Leaving the clients' plans: the client facet cannot bind on a template, so it goes. */
export function dropClientFacet(filter: PlanLibraryFilter): PlanLibraryFilter {
  return filter.clients.length === 0 ? filter : { ...filter, clients: [] };
}

/**
 * Add or remove one category, and one client.
 *
 * Returned rather than mutated, and these are the ONLY ways either selection changes — so "empty
 * means all" cannot be broken by a caller assembling its own array.
 */
export function toggleCategory(
  filter: PlanLibraryFilter,
  category: PlanCategoryKey,
): PlanLibraryFilter {
  const selected = filter.categories.includes(category);
  return {
    ...filter,
    categories: selected
      ? filter.categories.filter((candidate) => candidate !== category)
      : [...filter.categories, category],
  };
}

export function toggleClient(filter: PlanLibraryFilter, customerId: number): PlanLibraryFilter {
  const selected = filter.clients.includes(customerId);
  return {
    ...filter,
    clients: selected
      ? filter.clients.filter((candidate) => candidate !== customerId)
      : [...filter.clients, customerId],
  };
}

/**
 * The trainer's phrase, folded the way the rows already are.
 *
 * **Only the TERM is folded here.** Every row arrives from its projection carrying `searchText`,
 * folded once when the response landed — `model/searchFold` owns both foldings so they cannot
 * drift. Folding the rows here instead ran two NFD normalisations and six regex passes per row per
 * keystroke; on a hundred and twenty rows that is the whole of a character's budget spent
 * re-deriving a value that had not changed.
 */
function foldTerm(search: string): string {
  return foldForSearch(search.trim());
}

/** Does a row's category survive the TYP facet? Empty means all; an unrecognised category matches none. */
function passesCategory(
  filter: PlanLibraryFilter,
  categoryKey: PlanCategoryKey | null,
): boolean {
  if (filter.categories.length === 0) return true;
  return categoryKey !== null && filter.categories.includes(categoryKey);
}

/**
 * Does a row survive the TRYB facet?
 *
 * `calendar` is the NEGATION of the self-paced marker rather than an allow-list of its own, and that
 * is deliberate: `planLibraryRow` already refuses to mark a mode it cannot name, so everything
 * unmarked is what this build draws a calendar icon beside. The two guards agree by construction —
 * whatever the row calls Kalendarz, this facet calls Kalendarz.
 */
function passesMode(filter: PlanLibraryFilter, selfPaced: boolean): boolean {
  if (filter.mode === 'all') return true;
  return filter.mode === 'selfPaced' ? selfPaced : !selfPaced;
}

/**
 * Apply every facet that can bind on a TEMPLATE.
 *
 * The facets compose as AND, which is what makes the canon's no-results state reachable at all: two
 * categories and a phrase with no plan in common. Search runs over the name and the trainer's own
 * word for the category, so typing "mobilność" finds the same rows the Mobilność chip does — the
 * category facet is the precise version of a thing the search box already does approximately.
 */
export function filterTemplateRows(
  rows: readonly PlanLibraryRow[],
  filter: PlanLibraryFilter,
): readonly PlanLibraryRow[] {
  const term = foldTerm(filter.search);
  return rows.filter((row) => {
    if (!passesCategory(filter, row.categoryKey)) return false;
    if (!passesMode(filter, row.schedulingMarker === 'selfPaced')) return false;
    // A plan with no phases has no day count to compare, so it survives the facet rather than being
    // hidden by a figure it never claimed. `null` is an absence here, not a zero.
    if (filter.maxDays < NO_DAY_LIMIT && row.daysInCycle !== null && row.daysInCycle > filter.maxDays) {
      return false;
    }
    if (term === '') return true;
    return row.searchText.includes(term);
  });
}

/**
 * Apply every facet that can bind on a CLIENT'S PLAN.
 *
 * Three of the four, plus a search that matches the CLIENT as well as the plan — the scope's own
 * placeholder promises `Szukaj klienta lub planu`, and it is the client's name that the list is
 * titled by. Dni is absent for the reason in this file's header: the assignment carries no day count
 * and this app does not invent one.
 */
export function filterClientPlanRows(
  rows: readonly ClientPlanRow[],
  filter: PlanLibraryFilter,
): readonly ClientPlanRow[] {
  const term = foldTerm(filter.search);
  return rows.filter((row) => {
    if (!passesCategory(filter, row.categoryKey)) return false;
    if (!passesMode(filter, row.schedulingMarker === 'selfPaced')) return false;
    if (filter.clients.length > 0 && !filter.clients.includes(row.customerId)) return false;
    if (term === '') return true;
    return row.searchText.includes(term);
  });
}

/** One client as the Klient tab lists them: who they are, and how many live plans they hold. */
export interface ClientFacetOption {
  readonly customerId: number;
  readonly fullName: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly planCount: number;
}

/**
 * The Klient tab's own list, built from the rows the scope actually has.
 *
 * From the UNFILTERED rows, deliberately: a tab that listed only the clients surviving the current
 * selection would remove the option the trainer just chose the moment they chose it, and there
 * would be no way back to the full list. The canon builds it the same way — from `ASSIGN`, not from
 * the filtered view.
 *
 * The order is the rows' own, which `toClientPlanRows` has already put in surname order, so the
 * panel and the list agree without a second comparator.
 */
export function clientFacetOptions(
  rows: readonly ClientPlanRow[],
): readonly ClientFacetOption[] {
  const byClient = new Map<number, ClientFacetOption>();
  for (const row of rows) {
    const existing = byClient.get(row.customerId);
    byClient.set(row.customerId, {
      customerId: row.customerId,
      fullName: row.client.fullName,
      initials: row.client.initials,
      avatarUrl: row.client.avatarUrl,
      planCount: (existing?.planCount ?? 0) + 1,
    });
  }
  return [...byClient.values()];
}
